import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { loadEventById } from './backOfficeApi';

// THE EVENT LENS LIVES IN THE URL. `?event=<uuid>` is the single source of truth: a
// reload or a shared link reproduces the scope exactly, and clearing the param clears
// the scope. The router retains the param across navigations (retainSearchParams on
// the root route), so it survives moving between the 14 surfaces that read it.
//
// UUID, not slug: GET /api/event-pages/:id is zod-`.uuid()` and looks up by id only, so
// a slug would need a new endpoint. The uuid reuses loadEventById, which the entity
// registry already wires — no new backend surface, and one deterministic lookup.
//
// React state MIRRORS the param rather than competing with it: surfaces need the whole
// event object ({id, name, backendBacked}) for display, and the URL can only carry a key.
//
// Extracted verbatim from App.jsx so it stays ONE implementation — called once from the
// root shell today; migrated routes will call it directly instead of receiving it as a
// prop, without changing the URL param or cache key they share with not-yet-migrated ones.
export function useGlobalEventFilter() {
  const navigate = useNavigate();
  const eventParam = useRouterState({ select: (st) => st.location.search?.event }) || null;
  const [globalEventFilter, setGlobalEventFilterState] = useState(null);

  // Hydrate the object whenever the param and the loaded lens disagree — which covers a
  // cold deep link, a back/forward, and a hand-edited URL with one path. An id that
  // resolves to nothing clears the lens rather than leaving a scope nobody can see.
  useEffect(() => {
    if (!eventParam) { setGlobalEventFilterState(null); return undefined; }
    if (globalEventFilter?.id === eventParam) return undefined;
    let cancelled = false;
    loadEventById(eventParam)
      .then((e) => { if (!cancelled) setGlobalEventFilterState(e || null); })
      .catch(() => { if (!cancelled) setGlobalEventFilterState(null); });
    return () => { cancelled = true; };
  }, [eventParam, globalEventFilter?.id]);

  // Writing the lens writes the URL. State is set alongside so the picker doesn't flash
  // through "All events" while the hydrate round-trips — the param stays the truth, this
  // is just the object we already hold.
  const setGlobalEventFilter = useCallback((event) => {
    setGlobalEventFilterState(event || null);
    navigate({ replace: true, search: (prev) => ({ ...(prev || {}), event: event?.id || undefined }) });
  }, [navigate]);

  return [globalEventFilter, setGlobalEventFilter];
}
